import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Divider
} from '@mui/material';
import { format } from 'date-fns';
import { JobWorkReceivedItem } from '../../api/jobWorks';

interface InvoiceDetails {
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceAmount?: number;
  taxAmount?: number;
  invoiceImageUrl?: string;
}

interface ViewReceiptDialogProps {
  open: boolean;
  onClose: () => void;
  receipt: JobWorkReceivedItem;
  processMaterial: boolean;
  invoiceDetails: InvoiceDetails;
}

const ViewReceiptDialog: React.FC<ViewReceiptDialogProps> = ({
  open,
  onClose,
  receipt,
  processMaterial,
  invoiceDetails
}) => {
  // Calculate amount based on the type of receipt
  const quantity = processMaterial ? receipt.materialQuantity || 0 : receipt.pieceCount || 0;
  const rate = receipt.processingRate || 0;
  const amount = quantity * rate;

  // Calculate total from invoice details
  const subtotal = invoiceDetails.invoiceAmount || 0;
  const tax = invoiceDetails.taxAmount || 0;
  const total = subtotal + tax;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        View Receipt Details
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Receipt Information */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Receipt Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(receipt.createdAt), 'dd/MM/yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Design No
                  </Typography>
                  <Typography variant="body1">
                    {receipt.designNo || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Invoice Details */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Invoice Details
            </Typography>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Invoice Number
                  </Typography>
                  <Typography variant="body1">
                    {invoiceDetails.invoiceNumber || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Invoice Date
                  </Typography>
                  <Typography variant="body1">
                    {invoiceDetails.invoiceDate ? format(new Date(invoiceDetails.invoiceDate), 'dd/MM/yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body1">
                    ₹{subtotal.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tax Amount
                  </Typography>
                  <Typography variant="body1">
                    ₹{tax.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    ₹{total.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Invoice Image Preview */}
              {invoiceDetails.invoiceImageUrl && (
                <Box mt={2}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Invoice Image
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="img"
                      src={invoiceDetails.invoiceImageUrl}
                      alt="Invoice"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Received Items */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Received Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{processMaterial ? 'Material ID' : 'Garment Part'}</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>{processMaterial ? 'Material UOM' : 'UOM'}</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{processMaterial ? receipt.materialId : receipt.garmentPart}</TableCell>
                    <TableCell>{receipt.size}</TableCell>
                    <TableCell align="right">{quantity}</TableCell>
                    <TableCell>{processMaterial ? receipt.materialUom : 'PCS'}</TableCell>
                    <TableCell align="right">₹{rate.toFixed(2)}</TableCell>
                    <TableCell align="right">₹{amount.toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Additional Details */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Grid container spacing={2}>
                {!processMaterial && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Defect Quantity
                      </Typography>
                      <Typography variant="body1">
                        {receipt.defectQuantity || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Shortage Quantity
                      </Typography>
                      <Typography variant="body1">
                        {receipt.shortageQuantity || 0}
                      </Typography>
                    </Grid>
                  </>
                )}
                {receipt.remarks && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Remarks
                    </Typography>
                    <Typography variant="body1">
                      {receipt.remarks}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewReceiptDialog; 